import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, ButtonBase } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { FONT_SIZE, ICON_SIZE, NEUTRAL, SURFACE } from "@/theme/tokens";
import { TOUCH_TARGET_HEIGHT_SX } from "@/ui/touchTarget";
import type { OpenShiftRequest, ReplaceShiftRequest } from "@/utils/models";
import { GAPS_SHORTLIST_SIZE } from "../gapsRowLayout";
import { GapsApplicantLine } from "./GapsApplicantLine";
import type { GapsApplicant } from "./useGapsApplicants";

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	responseCount: number;
	/** The answers show; folded, the card offers one "see who" line instead. */
	isExpanded: boolean;
	applicants: GapsApplicant[];
	/** The user asked to see who answered. */
	onReview: () => void;
	formatHours: (start: DateTime, end: DateTime) => string;
	onApprove: (request: OpenShiftRequest | ReplaceShiftRequest, applicant: GapsApplicant) => void;
	/** The request asks for more than one job, so each response says which it is (`GapsApplicantLine`'s `showsJob`). */
	isMultiJob: boolean;
	testId: string;
};

/** The full-width rows under a card's answers — Option A's centred "Show responses", and "Show 2 more". */
const footerRowSx = {
	width: "100%",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	gap: 0.75,
	px: 2,
	py: 1,
	borderTop: `1px solid ${NEUTRAL[150]}`,
	bgcolor: SURFACE,
	fontSize: FONT_SIZE.EMPHASIS,
	fontWeight: 600,
	color: "primary.dark",
} as const;

/**
 * A request card's answers: folded, one "Show responses" row (the count is on the card's line above); open, the
 * responses on Option A's rail, each with a labelled Approve — three at first and the rest behind "Show N more".
 * Nothing for a request nobody answered.
 */
export const GapsRequestAnswers: FC<Props> = ({ request, responseCount, isExpanded, applicants, onReview, formatHours, onApprove, isMultiJob, testId }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const [isShowingAll, setIsShowingAll] = useState<boolean>(false);
	const visibleApplicants = isShowingAll ? applicants : applicants.slice(0, GAPS_SHORTLIST_SIZE);
	const hiddenCount = applicants.length - visibleApplicants.length;

	if (responseCount === 0) return null;
	if (!isExpanded) {
		return (
			<ButtonBase
				data-testid={`${testId}-review`}
				onClick={() => {
					track("gaps_requests_review_opened", { department_id: chosenDepartment.department_id, request_id: request.id, responses: responseCount });
					onReview();
				}}
				aria-expanded={false}
				sx={[footerRowSx, TOUCH_TARGET_HEIGHT_SX]}
			>
				{t("gaps.requests.showResponses")}
				<ExpandMoreIcon aria-hidden sx={{ fontSize: ICON_SIZE.MENU_ROW }} />
			</ButtonBase>
		);
	}
	return (
		<Box data-testid={`${testId}-applicants`} sx={{ borderTop: `1px solid ${NEUTRAL[150]}` }}>
			<Box sx={{ px: 2, pb: 0.5 }}>
				{visibleApplicants.map((applicant, index) => (
					<GapsApplicantLine
						key={applicant.rowKey}
						applicant={applicant}
						formatHours={formatHours}
						onApprove={(picked) => onApprove(request, picked)}
						showsJob={isMultiJob}
						isLast={index === visibleApplicants.length - 1}
						dataTestId={`${testId}-applicant-${applicant.nurseId}`}
					/>
				))}
			</Box>
			{hiddenCount > 0 && (
				<ButtonBase
					data-testid={`${testId}-show-more`}
					onClick={() => {
						track("gaps_requests_show_more", { department_id: chosenDepartment.department_id, request_id: request.id, hidden: hiddenCount });
						setIsShowingAll(true);
					}}
					sx={[footerRowSx, TOUCH_TARGET_HEIGHT_SX]}
				>
					{t("gaps.requests.showMore", { count: hiddenCount })}
				</ButtonBase>
			)}
		</Box>
	);
};
